import { useState, useRef, FormEvent } from 'react';
import { format } from 'date-fns';
import { CardConfig } from '../constants/cards.ts';

interface RSVPData {
  name: string;
  timestamp: string;
  pax: number;
  phone: string;
  organization: string;
  invitationFrom: string;
}

interface UseRSVPFormReturn {
  wishesData: RSVPData;
  setWishesData: React.Dispatch<React.SetStateAction<RSVPData>>;
  isSubmitting: boolean;
  submitError: string | null;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

export const useRSVPForm = (cardConfig: CardConfig): UseRSVPFormReturn => {
  const [wishesData, setWishesData] = useState<RSVPData>({
    name: '',
    timestamp: '',
    pax: 1,
    phone: '',
    organization: '',
    invitationFrom: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    const newRsvpEntry = {
      ...wishesData,
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      action: 'rsvp',
      pax: wishesData.pax || 1, // Ensure pax is at least 1 for RSVP
      organization: wishesData.organization || '',
      invitationFrom: wishesData.invitationFrom || '',
      cardId: cardConfig.id, // Include card ID to identify which event
      sheetName: cardConfig.googleSheetName // Include sheet name
    };

    try {
      // Send to Google Sheets with card-specific URL
      await fetch(
        cardConfig.googleAppsScriptUrl,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newRsvpEntry)
        }
      );

      // Also save to localStorage as backup with card-specific key
      const existingEntries = JSON.parse(localStorage.getItem(cardConfig.storageKey) || '[]');
      const updatedEntries = [...existingEntries, newRsvpEntry];
      localStorage.setItem(cardConfig.storageKey, JSON.stringify(updatedEntries));

      alert('RSVP successfully submitted!');
      
      // Reset form
      setWishesData({
        name: '',
        timestamp: '',
        pax: 1,
        phone: '',
        organization: '',
        invitationFrom: ''
      });
    } catch (error) {
      console.error('Error saving RSVP:', error);
      setSubmitError('Sorry, there was a problem submitting your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return {
    wishesData,
    setWishesData,
    isSubmitting,
    submitError,
    handleSubmit
  };
};
